import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Bone, Heart, MapPin, ShieldCheck, Sparkles, Truck } from "lucide-react";
import { api, fmt } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { lp, productAlt } from "../lib/localize";
import { SEO } from "../components/SEO";
import { FadeUp } from "../components/motion";

// Page d'atterrissage SEO/GEO « Médailles pour animaux » (demande du
// propriétaire, 2026-10-16) : section dédiée de la boutique avec toutes les
// optimisations — titre/description dédiés, mots-clés ciblés, JSON-LD
// (CollectionPage + ItemList + FAQPage + fil d'Ariane), balises géo (Léry,
// Montérégie, rive-sud de Montréal), FAQ riche pour les réponses des IA, et
// texte local. Produits : catégorie « Plaques d'identification » (39 modèles).
const CATEGORIE = "Plaques d'identification";

const KW_FR = "médaille pour chien, médaille pour chat, médaille animaux personnalisée, plaque d'identification chien, plaque d'identification chat, plaque d'identification animaux, tag chien personnalisé, plaque chien gravée, plaque chat gravée, médaille gravée pour animal, médaille os chien, médaille cœur chat, plaque médaille animaux pas chère, gravure médaille animaux Québec, médaille animaux Léry, plaques animaux Montérégie, médaille chien rive-sud Montréal, cadeau animal personnalisé";
const KW_EN = "dog tag, cat tag, personalized pet tag, pet ID tag, engraved dog tag, engraved cat tag, custom pet medal, bone dog tag, heart cat tag, pet tags Quebec, pet ID tags Montreal South Shore, pet tags Lery, engraved pet medals Canada";

const FAQ = [
  {
    fr: ["Combien coûte une médaille d'identification pour chien ou chat gravée ?",
         "Nos plaques d'identification pour animaux commencent à 14,99 $. Chaque plaque est gravée au laser avec le nom de votre animal et votre numéro de téléphone ; le prix exact de la personnalisation est confirmé avant la production, sans surprise."],
    en: ["How much does an engraved dog or cat ID tag cost?",
         "Our pet ID tags start at $14.99. Each tag is laser-engraved with your pet's name and your phone number; the exact personalization price is confirmed before production — no surprises."],
  },
  {
    fr: ["Quelles informations devrais-je graver sur la médaille de mon animal ?",
         "Le plus utile : le nom de votre chien ou chat et votre numéro de téléphone (une ou deux lignes). Vous pouvez ajouter une adresse ou une mention comme « pucé ». Nos plaques os, cœur et rondes offrent assez d'espace pour 1 à 4 lignes de texte net et durable."],
    en: ["What information should I engrave on my pet's tag?",
         "The essentials: your dog's or cat's name and your phone number (one or two lines). You can add an address or a note like “microchipped”. Our bone, heart and round tags have room for 1 to 4 lines of crisp, durable text."],
  },
  {
    fr: ["Où êtes-vous situés et livrez-vous partout au Canada ?",
         "Notre atelier de gravure est à Léry, en Montérégie (rive-sud de Montréal, Québec). Nous livrons partout au Canada par Postes Canada ou messagerie, et le ramassage à l'atelier est possible sur rendez-vous."],
    en: ["Where are you located, and do you ship across Canada?",
         "Our engraving workshop is in Léry, Montérégie (Montreal's South Shore, Quebec). We ship anywhere in Canada via Canada Post or courier, and workshop pickup is available by appointment."],
  },
  {
    fr: ["La gravure tient-elle dans le temps, même pour un chien actif ?",
         "Oui : la gravure laser creuse le métal anodisé — le texte ne s'efface pas comme une impression. Nos plaques sont légères, durables et offertes en plusieurs couleurs et formes (os, cœur, ronde, patte) pour chiens et chats de toutes tailles."],
    en: ["Does the engraving last, even for an active dog?",
         "Yes: laser engraving cuts into the anodized metal — the text won't rub off like a print. Our tags are light, durable and come in several colours and shapes (bone, heart, round, paw) for dogs and cats of all sizes."],
  },
];

export default function Animaux() {
  const { lang } = useLang();
  const fr = lang === "fr";
  const [produits, setProduits] = useState([]);

  useEffect(() => {
    api.get("/products/browse", { params: { category: CATEGORIE, limit: 60 } })
      .then(({ data }) => setProduits(data.products || []))
      .catch(() => {});
  }, []);

  const titre = fr
    ? "Médailles et plaques d'identification pour animaux gravées | Mr Trophée"
    : "Engraved pet ID tags and medals for dogs & cats | Mr Trophée";
  const desc = fr
    ? "Médailles pour animaux gravées au laser dès 14,99 $ : plaques d'identification pour chiens et chats (os, cœur, ronde, patte), nom et téléphone gravés durablement. Atelier à Léry, Montérégie — livraison partout au Canada."
    : "Laser-engraved pet medals from $14.99: ID tags for dogs and cats (bone, heart, round, paw), name and phone engraved to last. Workshop in Léry, Montérégie — shipping across Canada.";

  const breadcrumbLd = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: fr ? "Accueil" : "Home", item: "https://mrtrophee.ca/" },
      { "@type": "ListItem", position: 2, name: fr ? "La Boutique" : "Shop", item: "https://mrtrophee.ca/boutique" },
      { "@type": "ListItem", position: 3, name: fr ? "Médailles pour animaux" : "Pet medals", item: "https://mrtrophee.ca/boutique/animaux" },
    ],
  };
  const collectionLd = {
    "@context": "https://schema.org", "@type": "CollectionPage",
    name: fr ? "Médailles et plaques d'identification pour animaux" : "Pet ID tags and medals",
    description: desc, url: "https://mrtrophee.ca/boutique/animaux",
    inLanguage: fr ? "fr-CA" : "en-CA",
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: produits.length,
      itemListElement: produits.slice(0, 40).map((p, i) => ({
        "@type": "ListItem", position: i + 1,
        url: `https://mrtrophee.ca/produit/${p.id}`,
        name: lp(p, lang).name,
      })),
    },
  };
  const faqLd = {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: FAQ.map((f) => ({
      "@type": "Question",
      name: (fr ? f.fr : f.en)[0],
      acceptedAnswer: { "@type": "Answer", text: (fr ? f.fr : f.en)[1] },
    })),
  };

  return (
    <div className="bg-ink min-h-screen" data-testid="animaux-page">
      <SEO title={titre} description={desc} path="/boutique/animaux"
        keywords={fr ? KW_FR : KW_EN} jsonLd={[breadcrumbLd, collectionLd, faqLd]} />

      {/* Héros local (GEO : Léry, Montérégie, rive-sud de Montréal) */}
      <section className="border-b border-gold/10 bg-surface/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 md:py-20">
          <FadeUp>
            <p className="text-xs uppercase tracking-[0.3em] text-gold/80 mb-4" data-testid="animaux-kicker">
              {fr ? "La Boutique — Pour chiens et chats" : "The Shop — For dogs and cats"}
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl text-ivory leading-tight max-w-3xl" data-testid="animaux-h1">
              {fr
                ? <>Médailles pour animaux <span className="text-gold italic">gravées à vie</span></>
                : <>Pet medals <span className="text-gold italic">engraved for life</span></>}
            </h1>
            <p className="mt-5 text-ivory/70 max-w-2xl text-base leading-relaxed" data-testid="animaux-intro">
              {fr
                ? "Plaques d'identification personnalisées pour chiens et chats — formes os, cœur, ronde et patte, en plusieurs couleurs. Le nom de votre compagnon et votre téléphone sont gravés au laser dans le métal anodisé : un texte net qui ne s'efface jamais. Dès 14,99 $, produites à Léry (Montérégie, rive-sud de Montréal) et livrées partout au Canada."
                : "Personalized ID tags for dogs and cats — bone, heart, round and paw shapes, in several colours. Your companion's name and your phone number are laser-engraved into anodized metal: crisp text that never fades. From $14.99, made in Léry (Montérégie, Montreal's South Shore) and shipped across Canada."}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <a href="#modeles" data-testid="animaux-cta-modeles"
                className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-6 py-3 rounded-full hover:bg-champagne transition-colors duration-300 text-sm">
                {fr ? "Voir les 39 modèles" : "See the 39 models"} <ArrowRight className="w-4 h-4" />
              </a>
              <Link to="/soumission" data-testid="animaux-cta-soumission"
                className="inline-flex items-center gap-2 border border-gold/40 text-gold px-6 py-3 rounded-full hover:bg-gold hover:text-ink transition-colors duration-300 text-sm">
                {fr ? "Soumission en 30 secondes" : "30-second quote"}
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Atouts (texte riche pour moteurs et IA) */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: ShieldCheck, fr: ["Gravure qui dure", "Le laser creuse le métal anodisé : le texte ne s'efface pas, même après des années de jeux et de baignades."],
                                   en: ["Engraving that lasts", "The laser cuts into anodized metal: the text won't fade, even after years of play and swims."] },
            { icon: Bone,        fr: ["Formes et couleurs", "Os, cœur, ronde ou patte — en argent, noir, bleu, rose et plus, pour chiens et chats de toutes tailles."],
                                   en: ["Shapes and colours", "Bone, heart, round or paw — silver, black, blue, pink and more, for dogs and cats of all sizes."] },
            { icon: Truck,       fr: ["Livraison Canada", "Expédition par Postes Canada ou messagerie, partout au pays. Ramassage gratuit à l'atelier de Léry sur rendez-vous."],
                                   en: ["Canada-wide shipping", "Shipped by Canada Post or courier anywhere in the country. Free workshop pickup in Léry by appointment."] },
            { icon: MapPin,      fr: ["Atelier québécois", "Gravé à Léry, en Montérégie, par une entreprise d'ici — service en français et en anglais."],
                                   en: ["Quebec workshop", "Engraved in Léry, Montérégie, by a local business — service in French and English."] },
          ].map((b, i) => {
            const Icon = b.icon;
            const [h, p] = fr ? b.fr : b.en;
            return (
              <FadeUp key={i} delay={i * 0.06}>
                <div className="bg-surface/60 border border-ivory/10 rounded-2xl p-6 h-full" data-testid={`animaux-atout-${i}`}>
                  <Icon className="w-6 h-6 text-gold mb-3" />
                  <h2 className="font-serif text-lg text-ivory mb-1.5">{h}</h2>
                  <p className="text-sm text-ivory/65 leading-relaxed">{p}</p>
                </div>
              </FadeUp>
            );
          })}
        </div>
      </section>

      {/* Grille des modèles */}
      <section id="modeles" className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
        <FadeUp>
          <h2 className="font-serif text-3xl text-ivory mb-2" data-testid="animaux-modeles-h2">
            {fr ? "Tous les modèles" : "All models"}
          </h2>
          <p className="text-sm text-ivory/60 mb-8">
            {fr
              ? "Cliquez un modèle pour choisir la couleur, la grandeur et inscrire le texte à graver."
              : "Click a model to pick the colour, size and enter the text to engrave."}
          </p>
        </FadeUp>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-testid="animaux-grid">
          {produits.map((p) => (
            <Link key={p.id} to={`/produit/${p.id}`} data-testid={`animaux-product-${p.id}`}
              className="group bg-surface/60 border border-ivory/10 rounded-2xl overflow-hidden hover:border-gold/50 transition-colors duration-300">
              <div className="aspect-square bg-ink overflow-hidden">
                {p.image && (
                  <img src={p.image} alt={productAlt(p, lang)} loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                )}
              </div>
              <div className="p-4">
                <p className="text-sm text-ivory leading-snug line-clamp-2 group-hover:text-gold transition-colors">
                  {lp(p, lang).name}
                </p>
                <p className="mt-1 text-sm text-champagne font-semibold">{fmt(p.base_price)}</p>
              </div>
            </Link>
          ))}
        </div>
        {!produits.length && (
          <p className="text-ivory/50 text-sm py-10 text-center" data-testid="animaux-vide">
            {fr ? "Chargement des modèles…" : "Loading models…"}
          </p>
        )}
      </section>

      {/* FAQ (réponses des IA / position zéro) */}
      <section className="border-t border-gold/10 bg-surface/40">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-14">
          <FadeUp>
            <h2 className="font-serif text-3xl text-ivory mb-8 flex items-center gap-3" data-testid="animaux-faq-h2">
              <Sparkles className="w-6 h-6 text-gold" />
              {fr ? "Questions fréquentes — médailles pour animaux" : "FAQ — pet medals"}
            </h2>
          </FadeUp>
          <div className="space-y-6">
            {FAQ.map((f, i) => {
              const [q, a] = fr ? f.fr : f.en;
              return (
                <FadeUp key={i} delay={i * 0.05}>
                  <div className="bg-ink/60 border border-ivory/10 rounded-2xl p-6" data-testid={`animaux-faq-${i}`}>
                    <h3 className="font-semibold text-ivory mb-2">{q}</h3>
                    <p className="text-sm text-ivory/65 leading-relaxed">{a}</p>
                  </div>
                </FadeUp>
              );
            })}
          </div>
          <FadeUp delay={0.15}>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link to="/soumission" data-testid="animaux-faq-cta"
                className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-6 py-3 rounded-full hover:bg-champagne transition-colors duration-300 text-sm">
                {fr ? "Demander une soumission" : "Request a quote"} <ArrowRight className="w-4 h-4" />
              </Link>
              <p className="text-xs text-ivory/50">
                {fr
                  ? <>Une question ? <a href="tel:5145656665" className="text-gold">514-565-6665</a> — <Heart className="inline w-3 h-3" /> atelier à Léry, Montérégie.</>
                  : <>Questions? <a href="tel:5145656665" className="text-gold">514-565-6665</a> — <Heart className="inline w-3 h-3" /> workshop in Léry, Montérégie.</>}
              </p>
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
